/**
 * Problem-based charting (PBC) flag key + imperative getter.
 *
 * ONE MINTING SITE. The key and default are declared here and nowhere else;
 * `index.ts` imports them back for the hook counterpart
 * (`useIsProblemBasedChartingEnabled`). Before this file the pair was spelled
 * out twice - once for the hook and once for the imperative getter - so the
 * two could drift apart while still looking correct side by side.
 *
 * The key is a FROZEN EXTERNAL IDENTITY: Flagsmith targets the flag by it, and
 * ml-scribe reads the same string in
 * `features/release/release_problem_based_charting.py`. Renaming it here is
 * switching flags in two services at once, never a refactor.
 *
 * BOTH CALL STYLES ARE LOAD-BEARING, so this is not a stepping stone to
 * deleting the imperative one:
 *   - the hook is what UI should use. It re-renders when the flag resolves
 *     after mount, which matters because the provider settles asynchronously.
 *   - the getter below is for code that cannot call a hook. As of AME-964 it
 *     has exactly one caller, an async stream-close callback in
 *     `features/note-generation/stream-response/legacy/_stream-response.ts`,
 *     which runs outside React's render cycle entirely. Three hooks used to
 *     call it during render and each carried the same pre-hydration bug; they
 *     were migrated to the hook rather than left as precedent. A new hook
 *     caller is a bug, not a style choice - `grep` before adding one.
 *
 * Lives in a leaf `.ts` module rather than the barrel because a new export
 * there trips the `react-refresh/only-export-components` CI ratchet - same
 * reason as `offline-recording-flag-cache`.
 */
import { FEATURE_FLAG_DOMAIN } from '@/features/flags/constants';
import { OpenFeature } from '@openfeature/react-sdk';

/** Gates PBC end to end: problem extraction, ICD/CPT enrichment, Athena push. */
export const PROBLEM_BASED_CHARTING_FLAG_KEY = 'release-scribe-problem-based-charting';

/**
 * Off unless Flagsmith says otherwise, so a provider outage or a pre-hydration
 * read leaves PBC dormant instead of half-enabling a clinical flow.
 */
export const PROBLEM_BASED_CHARTING_FLAG_DEFAULT = false;

/**
 * Synchronous read for non-React callers.
 *
 * Returns the default until the OpenFeature provider is ready, and does not
 * re-run when the flag resolves later - a caller that renders off this value
 * wants the hook instead.
 */
export const isProblemBasedChartingEnabled = (): boolean =>
  OpenFeature.getClient(FEATURE_FLAG_DOMAIN).getBooleanValue(
    PROBLEM_BASED_CHARTING_FLAG_KEY,
    PROBLEM_BASED_CHARTING_FLAG_DEFAULT
  );
